import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const UserDashboard = () => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUserData = async () => {
            const token = localStorage.getItem('userToken');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const response = await fetch(`${apiUrl}/api/user/dashboard`, {
    headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    }
});


                const data = await response.json();

                if (response.ok) {
                    // Ensure we're using baseAmount for totalSaved calculation
                    if (data.transactions) {
                        data.totalSaved = data.transactions
                            .filter(t => t.status === 'succeeded')
                            .reduce((sum, t) => sum + t.baseAmount, 0);
                    }
                    setUserData(data);
                } else {
                    setError(data.message || 'Failed to load dashboard data');
                    if (response.status === 401) {
                        navigate('/login');
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Error loading dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
        const refreshInterval = setInterval(fetchUserData, 60000);
        return () => clearInterval(refreshInterval);
    }, [navigate]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-4xl mx-auto p-8">
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                    <p className="text-red-700">{error}</p>
                </div>
            </div>
        );
    }

    const nextPaymentDate = userData?.subscription?.nextPaymentDate ? 
        new Date(userData.subscription.nextPaymentDate) : null;

    const formattedNextPayment = nextPaymentDate?.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Calculate progress using baseAmount only
    const progressPercentage = userData?.subscription?.savingsGoal ? 
        ((userData.totalSaved || 0) / userData.subscription.savingsGoal) * 100 : 0;

    return (
        <div className="max-w-4xl mx-auto p-8">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Your Savings Dashboard</h1>
                    <p className="text-gray-600 mt-2">Welcome back, {userData?.email}</p>
                </div>
                <button
                    onClick={() => {
                        localStorage.removeItem('userToken');
                        navigate('/');
                    }}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                    Logout
                </button>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
                <h2 className="text-lg font-semibold text-blue-800">Next Payment Details</h2>
                <p className="text-blue-700">
                    Your next payment of ${userData?.subscription?.totalAmount.toFixed(2)} will be processed on {formattedNextPayment}
                </p>
                <div className="mt-2 text-sm">
                    <p>• ${userData?.subscription?.baseAmount.toFixed(2)} goes to your savings</p>
                    <p>• ${userData?.subscription?.feeAmount.toFixed(2)} transaction fee</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold mb-4">Total Saved</h2>
                    <p className="text-3xl font-bold text-blue-600">
                        ${(userData?.totalSaved || 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        Total amount in your savings (excluding fees)
                    </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h2 className="text-xl font-semibold mb-4">Monthly Contribution</h2>
                    <p className="text-3xl font-bold text-green-600">
                        ${userData?.subscription?.baseAmount?.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        Monthly amount to savings (excluding fees)
                    </p>
                </div>

                {userData?.subscription?.savingsGoal && (
                    <div className="bg-white p-6 rounded-xl shadow-lg md:col-span-2">
                        <h2 className="text-xl font-semibold mb-4">Progress to Goal</h2>
                        <div className="relative pt-1">
                            <div className="flex mb-2 items-center justify-between">
                                <div>
                                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                                        {progressPercentage.toFixed(1)}% Complete
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-semibold text-blue-600">
                                        ${(userData.totalSaved || 0).toFixed(2)} saved of ${userData.subscription.savingsGoal.toFixed(2)} goal
                                    </span>
                                </div>
                            </div>
                            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
                                <div
                                    style={{ width: `${progressPercentage}%` }}
                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                                ></div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white p-6 rounded-xl shadow-lg md:col-span-2">
                    <h2 className="text-xl font-semibold mb-4">Transaction History</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Savings Amount</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fee</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {userData?.transactions?.map((transaction, index) => (
                                    <tr key={index}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {new Date(transaction.chargeDate).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            ${transaction.baseAmount.toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap text-gray-500">
                                            ${transaction.feeAmount.toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap font-medium">
                                            ${transaction.totalAmount.toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded-full text-xs ${
                                                transaction.status === 'succeeded' 
                                                    ? 'bg-green-200 text-green-800' 
                                                    : 'bg-yellow-200 text-yellow-800'
                                            }`}>
                                                {transaction.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDashboard;